import { supabase } from '../supabase';
import { currentDivisionId } from '../stores/useFiltersStore';
import {
  Settlement,
  SinisterCalcParams,
  SinisterLossResult,
} from '../utils/types';

/**
 * List all settlements marked as sinister for a given exporter/season/species,
 * with optional filter by responsible party.
 */
export async function getSinisterSettlements(
  exporter_id: number | undefined,
  species_id: number | undefined,
  season?: string,
  responsible?: string,
  divisionId: number | null = currentDivisionId()
): Promise<Settlement[]> {
  try {
    let query = supabase
      .from('settlements')
      .select(
        `
        *,
        settlement_details:settlements_details(*),
        ship_trip:ship_trips(
          *,
          ship:ships(*),
          market:markets!ship_trips_market_id_fkey(*)
        ),
        market:markets!settlements_market_id_fkey(*),
        comparable_market:markets!settlements_comparable_market_id_fkey(*)
      `
      )
      .eq('exporter_id', exporter_id)
      .eq('species_id', species_id)
      .eq('is_sinister', true);

    if (season) query = query.eq('season', season);
    if (responsible) query = query.eq('sinister_responsible', responsible);
    if (divisionId) query = query.eq('division_id', divisionId);

    const { data, error } = await query;
    if (error) throw error;
    return (data as unknown as Settlement[]) || [];
  } catch (error) {
    console.error('Error getting sinister settlements:', error);
    return [];
  }
}

/**
 * Load a single sinister settlement by id with nested relations.
 */
export async function getSinisterSettlementById(
  id: number
): Promise<Settlement | null> {
  try {
    const { data, error } = await supabase
      .from('settlements')
      .select(
        `
        *,
        settlement_details:settlements_details(*),
        ship_trip:ship_trips(
          *,
          ship:ships(*),
          market:markets!ship_trips_market_id_fkey(*)
        ),
        market:markets!settlements_market_id_fkey(*),
        comparable_market:markets!settlements_comparable_market_id_fkey(*),
        exporter:exporters(*)
      `
      )
      .eq('id', id)
      .single();
    if (error) throw error;
    return data as unknown as Settlement;
  } catch (error) {
    console.error('Error getting sinister settlement:', error);
    return null;
  }
}

export interface ComparableShipTrip {
  id: number;
  ship_name: string;
  ship_type: string | null;
  eta: string;
  market_id: number | string | null;
  voyage_number: string | null;
}

/**
 * Load candidate ship_trips that match EITHER:
 *   - the settlement's sale market (`settlements.market_id`), or
 *   - the settlement's ship_trip POD (`ship_trips.market_id`)
 * Same species/season, optionally within an ETA window.
 *
 * Two filtered queries are issued and merged client-side because Supabase
 * doesn't cleanly support `.or()` across joined tables.
 */
export async function getComparableShipTrips(args: {
  /** Settlement's sale market. */
  market_id: string | number;
  /** Settlement's ship_trip POD (ship_trips.market_id). Optional. */
  pod_market_id?: string | number | null;
  species_id: number;
  season: string;
  ship_type?: 'M' | 'A' | 'T';
  eta_min?: string;
  eta_max?: string;
}): Promise<ComparableShipTrip[]> {
  const select = `
    ship_trip_id,
    ship_trip:ship_trips!inner(
      id,
      eta,
      market_id,
      voyage_number,
      ship:ships!inner(name, type)
    )
  `;

  const buildBase = () => {
    let q = supabase
      .from('settlements')
      .select(select)
      .eq('species_id', args.species_id)
      .eq('season', args.season);
    if (args.ship_type) q = q.eq('ship_trip.ship.type', args.ship_type);
    if (args.eta_min) q = q.gte('ship_trip.eta', args.eta_min);
    if (args.eta_max) q = q.lte('ship_trip.eta', args.eta_max);
    return q;
  };

  try {
    // Query A: ships whose settlement was sold to the same sale market.
    const queryA = buildBase().eq('market_id', args.market_id);
    // Query B: ships whose ship_trip POD matches the affected ship's POD.
    const queryB =
      args.pod_market_id != null
        ? buildBase().eq('ship_trip.market_id', args.pod_market_id)
        : null;

    const [resA, resB] = await Promise.all([
      queryA,
      queryB ? queryB : Promise.resolve({ data: [], error: null } as any),
    ]);

    if (resA.error) throw resA.error;
    if (resB.error) throw resB.error;

    const seen = new Map<number, ComparableShipTrip>();
    const ingest = (rows: any[]) => {
      for (const row of rows || []) {
        const st = row.ship_trip;
        if (!st || seen.has(st.id)) continue;
        seen.set(st.id, {
          id: st.id,
          ship_name: st.ship?.name || 'UNKNOWN',
          ship_type: st.ship?.type || null,
          eta: st.eta,
          market_id: st.market_id,
          voyage_number: st.voyage_number || null,
        });
      }
    };
    ingest((resA.data as any[]) || []);
    ingest((resB.data as any[]) || []);

    return Array.from(seen.values()).sort((a, b) =>
      (a.eta || '').localeCompare(b.eta || '')
    );
  } catch (error) {
    console.error('Error loading comparable ship trips:', error);
    return [];
  }
}

/**
 * Trigger the edge function to compute sinister loss. Results are returned
 * in-memory (not persisted).
 */
export async function calculateSinisterLoss(
  params: SinisterCalcParams
): Promise<SinisterLossResult[]> {
  try {
    const { data, error } = await supabase.functions.invoke(
      'calculate-sinister-loss',
      { body: params }
    );
    if (error) throw error;
    return (data?.results as SinisterLossResult[]) || [];
  } catch (error) {
    console.error('Error calculating sinister loss:', error);
    throw error;
  }
}

// ─── Persisting sinister certificates as claims ───

export type SinisterClaimSubreport =
  | 'sinister_market'
  | 'sinister_exporter'
  | 'sinister_industry';

const ACTIVE_CLAIM_STATUSES = ['draft', 'submitted', 'accepted', 'distributed'];
// Distributed claims have settlement_detail_revenues already applied, so they
// must be explicitly undistributed (in the Compensaciones page) before they
// can be replaced. Everything else is fair game to cancel on re-save.
const CANCELLABLE_ON_RESAVE = ['draft', 'submitted', 'accepted'];
const SINISTER_CLAIM_TYPES = [
  'sinister_market',
  'sinister_exporter',
  'sinister_industry',
] as const;

export interface SinisterClaimDetailInput {
  settlement_detail_id: number;
  comparable_per_kg: number;
  comparable_kilos: number;
  own_value_per_kg: number;
}

export interface SinisterClaimSaveInput {
  subreport: SinisterClaimSubreport;
  // common header fields
  exporter_id: number;
  receiver: string;
  season: string;
  total_amount: number; // in `currency`
  currency: 'RMB' | 'USD';
  description: string;
  notes?: string | null;
  // pool / discriminator metadata
  comparable_ship_trip_ids: number[];
  market_filter: string[]; // ['SH'] / ['GZ'] for market, [] otherwise
  incoterm?: 'FOB' | 'CIF' | null; // only meaningful for exporter / industry
  comparison_target?: 'median' | 'average';
  pool_methodology?: 'by_volume' | 'by_exporter' | null; // only for industry
  market_reference_date?: string | null; // YYYY-MM-DD, only for market
  exchange_rate_used?: number | null; // only for market when displayed in USD
  // detail rows
  details: SinisterClaimDetailInput[];
}

/**
 * Persist a single sinister certificate (one subreport) as a draft claim.
 * Inserts the parent row in `claims` and the per-line rows in `claims_details`
 * inside a logical sequence (no DB transaction available via supabase-js,
 * so on details failure the parent is rolled back manually).
 */
export async function createSinisterClaim(
  input: SinisterClaimSaveInput
): Promise<{ claim_id: number }> {
  // Insert the parent claim
  const { data: claim, error: claimError } = await supabase
    .from('claims')
    .insert({
      exporter_id: input.exporter_id,
      receiver: input.receiver,
      season: input.season,
      status: 'draft',
      description: input.description,
      notes: input.notes || null,
      total_claim_usd: input.total_amount,
      currency: input.currency,
      comparison_type: input.subreport,
      sinister_filter: 'sinister',
      sinister_filter_comparison: 'non_sinister',
      market_filter: input.market_filter.length ? input.market_filter : null,
      filtered_ship_trips:
        input.comparable_ship_trip_ids.length > 0
          ? input.comparable_ship_trip_ids.map((id) => String(id))
          : null,
      incoterm: input.incoterm || null,
      comparison_target: input.comparison_target || null,
      pool_methodology: input.pool_methodology || null,
      market_reference_date: input.market_reference_date || null,
      exchange_rate_used: input.exchange_rate_used ?? null,
    })
    .select('id')
    .single();

  if (claimError || !claim) {
    throw claimError || new Error('Failed to create claim');
  }

  if (input.details.length === 0) return { claim_id: claim.id };

  const detailRows = input.details.map((d) => ({
    claim_id: claim.id,
    settlement_detail_id: d.settlement_detail_id,
    comparable_fob_per_kg: d.comparable_per_kg,
    comparable_kilos: d.comparable_kilos,
    own_value_per_kg: d.own_value_per_kg,
  }));

  const { error: detailsError } = await supabase
    .from('claims_details')
    .insert(detailRows);

  if (detailsError) {
    // best-effort rollback
    await supabase.from('claims').delete().eq('id', claim.id);
    throw detailsError;
  }

  return { claim_id: claim.id };
}

/**
 * Cancel any active sinister claims that point to the given settlement before
 * persisting a fresh batch — so re-saving a sinister certificate doesn't leave
 * stale draft claims behind. `accepted` and `distributed` claims are NEVER
 * touched.
 */
export async function cancelActiveSinisterClaimsForSettlement(
  settlement_id: number
): Promise<number> {
  // 1. Resolve all settlement_details for this settlement
  const { data: details, error: detailsError } = await supabase
    .from('settlements_details')
    .select('id')
    .eq('settlement_id', settlement_id);
  if (detailsError) {
    console.error('Error loading details for sinister cancel:', detailsError);
    return 0;
  }
  const detailIds = (details || []).map((d) => Number(d.id));
  if (detailIds.length === 0) return 0;

  // 2. Fetch sinister claims that are cancellable on re-save (everything
  //    except distributed). RLS scopes by exporter.
  const { data: claims, error: claimsError } = await supabase
    .from('claims')
    .select('id')
    .in('comparison_type', SINISTER_CLAIM_TYPES as unknown as string[])
    .in('status', CANCELLABLE_ON_RESAVE);
  if (claimsError) {
    console.error('Error loading sinister claims for cancel:', claimsError);
    return 0;
  }
  if (!claims || claims.length === 0) return 0;
  const candidateClaimIds = claims.map((c) => Number(c.id));

  // 3. Of those, keep only the ones that have at least one detail pointing
  //    to this specific settlement.
  const { data: cdRows, error: cdError } = await supabase
    .from('claims_details')
    .select('claim_id')
    .in('claim_id', candidateClaimIds)
    .in('settlement_detail_id', detailIds);
  if (cdError) {
    console.error('Error finding sinister claim details:', cdError);
    return 0;
  }
  const claimIds = Array.from(
    new Set((cdRows || []).map((r: any) => Number(r.claim_id)))
  );
  if (claimIds.length === 0) return 0;

  const { error: cancelError } = await supabase
    .from('claims')
    .update({ status: 'cancelled' })
    .in('id', claimIds);
  if (cancelError) throw cancelError;
  return claimIds.length;
}

/**
 * Map of `settlement_id` → presence of saved sinister claims per subreport.
 * Used by the Siniestros list page to render the three "saved?" indicator
 * columns. Cancelled / denied claims are ignored.
 */
export interface SinisterClaimPresence {
  market: { id: number; status: string } | null;
  exporter: { id: number; status: string } | null;
  industry: { id: number; status: string } | null;
}

export async function getSinisterClaimsBySettlementIds(
  settlement_ids: number[]
): Promise<Map<number, SinisterClaimPresence>> {
  const result = new Map<number, SinisterClaimPresence>();
  if (settlement_ids.length === 0) return result;

  // Step 1: resolve detail_id → settlement_id for the visible sinisters.
  const { data: details, error: detailsError } = await supabase
    .from('settlements_details')
    .select('id, settlement_id')
    .in('settlement_id', settlement_ids);

  if (detailsError) {
    console.error(
      'Error loading settlement details for claim presence:',
      detailsError
    );
    return result;
  }
  if (!details || details.length === 0) return result;

  const detailToSettlement = new Map<number, number>();
  for (const d of details) {
    detailToSettlement.set(Number(d.id), Number(d.settlement_id));
  }
  const detailIds = Array.from(detailToSettlement.keys());

  // Step 2: fetch all active sinister claims visible to the user (RLS scopes
  // by exporter). Fetching the parent table directly avoids supabase-js
  // nested-filter quirks where `.in('claims.column', ...)` on an embedded
  // resource may be silently dropped.
  const { data: claims, error: claimsError } = await supabase
    .from('claims')
    .select('id, comparison_type, status')
    .in('comparison_type', SINISTER_CLAIM_TYPES as unknown as string[])
    .in('status', ACTIVE_CLAIM_STATUSES);

  if (claimsError) {
    console.error('Error loading sinister claims:', claimsError);
    return result;
  }
  if (!claims || claims.length === 0) return result;

  const claimsById = new Map<number, { id: number; comparison_type: string; status: string }>();
  for (const c of claims) {
    claimsById.set(Number(c.id), {
      id: Number(c.id),
      comparison_type: String(c.comparison_type),
      status: String(c.status),
    });
  }
  const claimIds = Array.from(claimsById.keys());

  // Step 3: pull claims_details that link those claims to the visible details.
  const { data: cdRows, error: cdError } = await supabase
    .from('claims_details')
    .select('claim_id, settlement_detail_id')
    .in('claim_id', claimIds)
    .in('settlement_detail_id', detailIds);

  if (cdError) {
    console.error('Error loading claim details for presence:', cdError);
    return result;
  }

  const seen = new Set<string>();
  for (const row of (cdRows as any[]) || []) {
    const sid = detailToSettlement.get(Number(row.settlement_detail_id));
    const c = claimsById.get(Number(row.claim_id));
    if (!sid || !c) continue;

    // dedupe per (settlement_id, comparison_type) — multiple details map to
    // the same claim, but we only want one entry per (settlement, source).
    const dedupeKey = `${sid}|${c.comparison_type}`;
    if (seen.has(dedupeKey)) continue;
    seen.add(dedupeKey);

    let bucket = result.get(sid);
    if (!bucket) {
      bucket = { market: null, exporter: null, industry: null };
      result.set(sid, bucket);
    }
    const slot: keyof SinisterClaimPresence =
      c.comparison_type === 'sinister_market'
        ? 'market'
        : c.comparison_type === 'sinister_exporter'
        ? 'exporter'
        : 'industry';
    bucket[slot] = { id: c.id, status: c.status };
  }
  return result;
}
